import { useState, type CSSProperties } from 'react'
import { isNativePlatform } from '@/lib/platform'

/**
 * iOS WKWebView has a secure-text-entry bug: a native `type=password` field
 * silently keeps only the last few programmatically-entered characters (e2e
 * typed "password123" -> field held 3 chars -> invalid_grant). ONLY inside
 * the Capacitor native shell, render a non-secure `type=text` input visually
 * masked via `-webkit-text-security` (a WebKit-only CSS the WKWebView
 * supports) so the password is masked yet types reliably. The web keeps a
 * real `type=password` (Chromium/Firefox/Safari + Playwright + password
 * managers + the vitest `input[type=password]` selector, all unaffected).
 *
 * Extracted from LoginPage.tsx (EP-150) so every password field that can be
 * opened inside the native shell shares one masking mechanism instead of
 * copying the WebkitTextSecurity dance at each call site.
 */
export function useMaskedPasswordField() {
  const [showPassword, setShowPassword] = useState(false)
  const nativeShell = isNativePlatform()

  const type = nativeShell ? 'text' : showPassword ? 'text' : 'password'
  const style: CSSProperties | undefined = nativeShell
    ? ({ WebkitTextSecurity: showPassword ? 'none' : 'disc' } as CSSProperties)
    : undefined

  return {
    isNativeShell: nativeShell,
    showPassword,
    toggle: () => setShowPassword((prev) => !prev),
    type,
    style,
  }
}
